import React, { useCallback, useEffect, useRef } from 'react'
import styled from 'styled-components';
import { BREAKPOINT } from '../hooks/DeviceContext';
import Slider from './Slides/Slider';

export default function ArchiveProject({
  title,
  titleColor,
  subtitle,
  description,
  images,
  link,
  linkText,
  linkTextExtra,
}) {
  const wrapperRef = useRef(null);
  const firstImageRef = useRef(null);

  const handleResize = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (wrapper === null) return;
    const firstImage = firstImageRef.current;
    if (firstImage === null) return;
    const wrapperWidth = wrapper.offsetWidth;
    const { naturalWidth, naturalHeight } = firstImage
    const newHeight = wrapperWidth / naturalWidth * naturalHeight;
    wrapper.style.height = `${newHeight}px`;
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (wrapper === null) return;
    const firstImage = wrapper.querySelector('.slide-image');
    if (firstImage === null) return;

    const onLoad = () => {
      firstImageRef.current = firstImage;
      handleResize();
    }

    if (firstImage.complete && firstImage.naturalWidth) {
      onLoad();
    } else {
      firstImage.onload = onLoad;
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [images, handleResize]);

  return (
    <Wrapper>
      <Slideshow ref={wrapperRef}>
        <Slider images={images} />
      </Slideshow>
      <Info $titleColor={titleColor}>
        <h2>{title}</h2>
        <h3>{subtitle}</h3>
      </Info>
      <Description>
        <p>{description}</p>
        {link &&
          <div>
            <a href={link} target='_blank' rel="noreferrer">
              {linkText || 'See more on Behance'}
            </a>
            {linkTextExtra && <span> {linkTextExtra}</span>}
          </div>
        }
      </Description>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Slideshow = styled.div`
  width: 100%;
  position: relative;
`;

const Info = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 0.625rem;
  margin-top: 1.25rem;

  h2 {
    font-size: var(--font-size-m-mob);
    font-family: var(--main-font);
    font-weight: 700;
    font-style: italic;
    line-height: 1.25rem;
    color: var(--blue);
    text-align: center;
  }

  h3 {
    font-size: var(--font-size-m-mob);
    font-family: var(--main-font);
    font-weight: 400;
    font-style: italic;
    line-height: 1.25rem;
    color: var(--blue);
  }
`;

const Description = styled.div`
  margin-top: 1.25rem;

  p {
    font-size: var(--font-size-s-mob);
    font-family: var(--secondary-font);
    font-weight: 100;
    line-height: 0.938rem;
    color: var(--blue);

    text-indent: 1.3rem;
  }

  div {
    margin-top: 0.5rem;
  }

  a, span {
    font-size: var(--font-size-s-mob);
    font-family: var(--main-font);
    font-weight: 700;
    line-height: 0.875rem;
    color: var(--green);
  }

  span {
    font-weight: 400;
  }

  @media (min-width: ${BREAKPOINT}px) {
    p, a, span {
      font-size: var(--font-size-s-desk);
    }
  }
`;
